import React from "react";
import { motion } from "framer-motion";
import { AiFillStar, AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import { FaShoppingCart } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import https from "../../utils/https";
import {
  isInWishlist as checkWishlist,
  toggleWishlist,
} from "../../utils/wishlistSync";
import { fetchCartItems } from "../../utils/cartSync";
import { toast } from "../../utils/toast";

const PRODUCT_PLACEHOLDER = "/images/products/placeholder.png";

interface ProductCardProps {
  id?: string | number;
  _id?: string;
  image: string;
  name: string;
  star?: string | number;
  price: string | number;
  originalPrice?: string | number;
  off?: string;
  ratingCount?: string;
  isHotDeal?: boolean;
  tag?: string;
  images?: string[];
  variants?: any[];
  is_featured?: boolean;
  is_new?: boolean;
  emi?: string;
  hideAddToCart?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  id,
  _id,
  image,
  name,
  star,
  price,
  originalPrice,
  off,
  ratingCount,
  isHotDeal,
  tag,
  images,
  variants,
  is_featured,
  is_new,
  emi,
  hideAddToCart = false,
}) => {
  const cleanId =
    _id && String(_id) !== "undefined"
      ? String(_id)
      : id && String(id) !== "undefined"
        ? String(id)
        : null;
  const navigate = useNavigate();

  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isInWishlist, setIsInWishlist] = React.useState(
    checkWishlist(cleanId || undefined),
  );
  const [isMobile, setIsMobile] = React.useState(false);
  const [isAdded, setIsAdded] = React.useState(false);
  const [adding, setAdding] = React.useState(false);

  React.useEffect(() => {
    setIsInWishlist(checkWishlist(cleanId || undefined));

    const handleWishlistUpdate = () => {
      setIsInWishlist(checkWishlist(cleanId || undefined));
    };

    const handleCartUpdate = () => {
      const currentCartStr = localStorage.getItem("honeywill_cart");
      const cartList: any[] = currentCartStr ? JSON.parse(currentCartStr) : [];
      setIsAdded(
        cleanId ? cartList.some((item) => item.id === cleanId) : false,
      );
    };

    window.addEventListener("wishlistUpdated", handleWishlistUpdate);
    window.addEventListener("cartUpdated", handleCartUpdate);

    handleCartUpdate();

    return () => {
      window.removeEventListener("wishlistUpdated", handleWishlistUpdate);
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, [cleanId]);

  // Centralize price and original price resolution logic
  const resolvedPrice = React.useMemo(() => {
    if (!price) return 0;
    if (Array.isArray(price)) {
      const match = price.find(
        (p: any) => p && (p.type === "single" || p.type === "coil")
      );
      if (match && typeof match.price === "number") return match.price;
      const firstPriceObj = price[0];
      if (firstPriceObj && typeof firstPriceObj.price === "number") {
        return firstPriceObj.price;
      }
      return 0;
    }
    if (typeof price === "number") return price;
    return parseFloat(String(price).replace(/[^\d.]/g, "")) || 0;
  }, [price]);

  const resolvedOriginalPrice = React.useMemo(() => {
    if (originalPrice) {
      if (Array.isArray(originalPrice)) {
        const match = originalPrice.find(
          (p: any) => p && (p.type === "single" || p.type === "coil")
        );
        if (match && typeof match.price === "number") return match.price;
        const firstPriceObj = originalPrice[0];
        if (firstPriceObj && typeof firstPriceObj.price === "number") {
          return firstPriceObj.price;
        }
        return 0;
      }
      if (typeof originalPrice === "number") return originalPrice;
      return parseFloat(String(originalPrice).replace(/[^\d.]/g, "")) || 0;
    }
    // Fallback: If price is an array, we can check if the matched price object has an `mrp` field.
    if (Array.isArray(price)) {
      const match = price.find(
        (p: any) => p && (p.type === "single" || p.type === "coil")
      );
      if (match && typeof match.mrp === "number") return match.mrp;
      const firstPriceObj = price[0];
      if (firstPriceObj && typeof firstPriceObj.mrp === "number") {
        return firstPriceObj.mrp;
      }
    }
    return 0;
  }, [originalPrice, price]);

  // Calculate discount percentage
  const discountPercentage = React.useMemo(() => {
    if (resolvedPrice && resolvedOriginalPrice && resolvedOriginalPrice > resolvedPrice) {
      return Math.round(((resolvedOriginalPrice - resolvedPrice) / resolvedOriginalPrice) * 100);
    }
    return 0;
  }, [resolvedPrice, resolvedOriginalPrice]);

  // Format display discount
  const displayDiscount = React.useMemo(() => {
    if (discountPercentage > 0) {
      return `${discountPercentage}% OFF`;
    }
    if (off && !off.includes("NaN") && !off.startsWith("0")) {
      return off.toUpperCase().replace("OFF", "").trim() + " OFF";
    }
    return null;
  }, [discountPercentage, off]);

  // Calculate savings if possible
  const savings = React.useMemo(() => {
    if (resolvedPrice && resolvedOriginalPrice) {
      if (resolvedOriginalPrice > resolvedPrice) {
        return Math.round(resolvedOriginalPrice - resolvedPrice);
      }
    }
    return null;
  }, [resolvedPrice, resolvedOriginalPrice]);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (adding) return;
    setAdding(true);
    try {
      const cleanId =
        _id && String(_id) !== "undefined"
          ? String(_id)
          : id && String(id) !== "undefined"
            ? String(id)
            : null;
      if (!cleanId) {
        console.error(
          "ProductCard: Could not resolve a valid product ID. Destructured props:",
          { _id, id, name },
        );
        return;
      }

      const parsedPrice = resolvedPrice;
      const parsedOriginalPrice = resolvedOriginalPrice || resolvedPrice * 1.15;

      const cartItem = {
        id: cleanId,
        name: name,
        image: image || (images && images[0]) || PRODUCT_PLACEHOLDER,
        ram: variants?.[0]?.name || "Standard",
        subtotal: parsedPrice,
        cuttoff: `₹${Math.ceil(parsedOriginalPrice).toLocaleString()}`,
        no: 1,
        priceA: parsedPrice,
        purchaseMode: "coil",
      };

      const isAuth =
        typeof window !== "undefined" && !!localStorage.getItem("token");
      if (isAuth) {
        await https.post("cart/add", { product_id: cleanId, quantity: 1 });
        await fetchCartItems();
      } else {
        const currentCartStr = localStorage.getItem("honeywill_cart");
        const cartList: any[] = currentCartStr ? JSON.parse(currentCartStr) : [];

        const existingIndex = cartList.findIndex(
          (item) => item.id === cartItem.id,
        );
        if (existingIndex > -1) {
          cartList[existingIndex].no = 1;
          cartList[existingIndex].subtotal = cartList[existingIndex].priceA;
        } else {
          cartList.push(cartItem);
        }

        localStorage.setItem("honeywill_cart", JSON.stringify(cartList));
      }

      window.dispatchEvent(new Event("cartUpdated"));
      toast.success("Added to Cart successfully!");

      setIsAdded(true);
    } catch (error) {
      console.error("Error adding to cart in ProductCard:", error);
    } finally {
      setAdding(false);
    }
  };

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Logic to determine display images
  const displayImages = React.useMemo(() => {
    let result: string[] = [];
    const getUrl = (img: any): string => {
      if (typeof img === "string") return img;
      return img?.url || "";
    };

    if (variants && variants.length > 0) {
      result = variants.map((v) => getUrl(v.image)).filter((img) => !!img);
    } else if (images && images.length > 0) {
      result = images.map((img) => getUrl(img)).filter((img) => !!img);
    }

    if (result.length === 0 && image) {
      result = [getUrl(image)];
    }

    return result.length > 0 ? result : [PRODUCT_PLACEHOLDER];
  }, [variants, images, image]);

  // Auto-scroll logic for multiple images (only on hover)
  React.useEffect(() => {
    if (!isHovered || displayImages.length <= 1) {
      setCurrentImageIndex(0);
      return;
    }
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % displayImages.length);
    }, 1500);
    return () => clearInterval(timer);
  }, [isHovered, displayImages]);

  // Extract Brand and Subtitle
  const { brand, description } = React.useMemo(() => {
    const firstSpaceIdx = name.indexOf(" ");
    const b = firstSpaceIdx > -1 ? name : name;
    const d = firstSpaceIdx > -1 ? name : "";
    return { brand: b, description: d };
  }, [name]);

  // Extract Unique Sizes
  const sizes = React.useMemo(() => {
    if (!variants || variants.length === 0) return "Onesize";
    const uniqueSizes = Array.from(
      new Set(variants.map((v) => v.size || v.attribute_name).filter(Boolean)),
    );
    return uniqueSizes.length > 0 ? uniqueSizes.join(", ") : "Onesize";
  }, [variants]);

  const cleanRatingCount = React.useMemo(() => {
    if (!ratingCount) return "28";
    return ratingCount;
  }, [ratingCount]);

  const handleToggleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!cleanId) return;

    const productObj = {
      _id: cleanId,
      name,
      price: resolvedPrice,
      images: displayImages.map((url) => ({ url })),
    };
    await toggleWishlist(productObj);
  };

  const linkTo = cleanId ? `/product/${cleanId}` : "#";

  return (
    <Link
      to={linkTo}
      className="hw-product-card-link"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <style>
        {`
                .hw-product-card-link {
                    text-decoration: none;
                    color: inherit;
                    display: block;
                    width: 100%;
                }
                .hw-product-card {
                    background: white;
                    display: flex;
                    flex-direction: column;
                    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
                    position: relative;
                    width: 100%;
                    border-radius: 12px;
                    border: 1px solid #7e7e7e53;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                }
                .hw-product-card:hover {
                    box-shadow: 0 12px 28px rgba(0,0,0,0.1);
                    border-color: #43c8ca;
                    transform: translateY(-6px);
                    z-index: 10;
                }
                .hw-image-container {
                    position: relative;
                    width: 100%;
                    aspect-ratio: 1 / 0.95;
                    overflow: hidden;
                    background-color: #f7f8fa;
                }
                .hw-product-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .hw-badge-container {
                    position: absolute;
                    top: 10px;
                    left: 10px;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    z-index: 2;
                }
                .hw-badge-off {
                    background: #43c8ca;
                    color: white;
                    font-size: 10px;
                    font-weight: 800;
                    padding: 4px 8px;
                    text-transform: uppercase;
                    border-radius: 6px;
                    letter-spacing: 0.5px;
                    align-self: flex-start;
                    box-shadow: 0 4px 8px rgba(67, 200, 202, 0.2);
                }
                .hw-badge-tag {
                    background: linear-gradient(135deg, #fdd835 0%, #fbc02d 100%);
                    color: #282c3f;
                    font-size: 10px;
                    font-weight: 800;
                    padding: 4px 8px;
                    text-transform: uppercase;
                    border-radius: 6px;
                    letter-spacing: 0.5px;
                    align-self: flex-start;
                    box-shadow: 0 4px 8px rgba(253, 216, 53, 0.2);
                }
                .hw-ad-badge {
                    background-color: rgba(0,0,0,0.6);
                    color: white;
                    font-size: 9px;
                    font-weight: 700;
                    padding: 3px 6px;
                    text-transform: uppercase;
                    border-radius: 4px;
                    letter-spacing: 0.5px;
                    align-self: flex-start;
                }
                .hw-wishlist-icon-btn {
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    width: 32px;
                    height: 32px;
                    background: rgba(255, 255, 255, 0.85);
                    backdrop-filter: blur(4px);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    cursor: pointer;
                    z-index: 5;
                    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }
                .hw-wishlist-icon-btn:hover {
                    transform: scale(1.15);
                    background: #ffffff;
                    box-shadow: 0 6px 16px rgba(67, 200, 202, 0.15);
                }
                .hw-rating-badge {
                    position: absolute;
                    bottom: 10px;
                    left: 10px;
                    background: rgba(255, 255, 255, 0.85);
                    backdrop-filter: blur(4px);
                    padding: 4px 8px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 11px;
                    font-weight: 700;
                    color: #282c3f;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    z-index: 2;
                    pointer-events: none;
                    border: 1px solid rgba(255, 255, 255, 0.4);
                }
                .hw-rating-star {
                    color: #149a8f;
                    font-size: 12px;
                }
                .hw-rating-separator {
                    color: #d4d5d9;
                    margin: 0 2px;
                }
                .hw-rating-count {
                    color: #282c3f;
                }
                .hw-product-info {
                    padding: 12px;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    background: #fff;
                    position: relative;
                    width: 100%;
                    box-sizing: border-box;
                    border-top: 1px solid #f3f4f6;
                }
                .hw-product-brand {
                    font-size: 13px;
                    font-weight: 700;
                    color: #1a1a1a;
                    margin: 0;
                    text-transform: capitalize;
                    text-align: left;
                    letter-spacing: 0.3px;
                }
                .hw-product-subtitle {
                    font-size: 11.5px;
                    color: #6b7280;
                    margin: 0;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    text-align: left;
                }
                .hw-price-row {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-top: 2px;
                    flex-wrap: wrap;
                }
                .hw-price-current {
                    font-size: 13.5px;
                    font-weight: 800;
                    color: #43c8ca;
                }
                .hw-price-original {
                    font-size: 11px;
                    color: #9ca3af;
                    text-decoration: line-through;
                }
                .hw-price-save {
                    font-size: 10.5px;
                    font-weight: 700;
                    color: #10b981;
                }
                .hw-emi-row {
                    margin-top: 4px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .hw-emi-text {
                    font-size: 11px;
                    color: #7e808c;
                }
                .hw-emi-badge {
                    background: #282c3f;
                    color: white;
                    font-size: 9px;
                    font-weight: 700;
                    padding: 1px 4px;
                    border-radius: 2px;
                    letter-spacing: 0.5px;
                }
                
                /* Dots container */
                .hw-carousel-dots {
                    position: absolute;
                    bottom: 12px;
                    left: 0;
                    right: 0;
                    display: flex;
                    justify-content: center;
                    gap: 5px;
                    z-index: 3;
                    pointer-events: none;
                    transition: opacity 0.3s ease;
                }
                .hw-dot {
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    background-color: #d4d5d9;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                    transition: all 0.25s ease;
                }
                .hw-dot.active {
                    background-color: #43c8ca;
                    width: 12px;
                    border-radius: 3px;
                }
                
                /* Similar Items hover button */
                .hw-similar-btn {
                    position: absolute;
                    bottom: 12px;
                    right: 12px;
                    width: 32px;
                    height: 32px;
                    background: #ffffff;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.12);
                    cursor: pointer;
                    z-index: 3;
                    transition: transform 0.2s ease, opacity 0.3s ease;
                }
                .hw-similar-btn:hover {
                    transform: scale(1.1);
                }
                
                /* Action Button Row container */
                .hw-card-actions-wrapper {
                    display: flex;
                    gap: 8px;
                    margin-top: 8px;
                    width: 100%;
                    transition: all 0.3s ease;
                }
                
                .hw-btn-add-to-cart {
                    flex: 1;
                    background: #43c8ca;
                    color: white;
                    border: none;
                    font-size: 11px;
                    font-weight: 700;
                    padding: 8px 12px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .hw-btn-add-to-cart:hover {
                    background: #dbeb83;
                    color: #3d4800;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(219, 235, 131, 0.4);
                }
                .hw-btn-add-to-cart.added {
                    background: #10b981;
                }
                .hw-btn-add-to-cart.added:hover {
                    background: #059669;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
                }
                
                .hw-btn-wishlist-toggle {
                    width: 32px;
                    height: 32px;
                    background: #f3f4f6;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    color: #4b5563;
                }
                .hw-btn-wishlist-toggle:hover {
                    background: #fee2e2;
                    border-color: #fecaca;
                    color: #ef4444;
                }
                .hw-btn-wishlist-toggle.active {
                    background: #fee2e2;
                    border-color: #fca5a5;
                    color: #ef4444;
                }

                @media (max-width: 768px) {
                    .hw-product-info {
                        padding: 8px !important;
                    }
                    .hw-product-brand {
                        font-size: 12px !important;
                    }
                    .hw-product-subtitle {
                        font-size: 10px !important;
                    }
                    .hw-price-current {
                        font-size: 12px !important;
                    }
                    .hw-price-original, .hw-price-save {
                        font-size: 9.5px !important;
                    }
                    .hw-btn-add-to-cart {
                        padding: 6px 8px !important;
                        font-size: 10px !important;
                        border-radius: 6px !important;
                    }
                    .hw-btn-wishlist-toggle {
                        width: 28px !important;
                        height: 28px !important;
                        border-radius: 6px !important;
                    }
                    .hw-rating-badge {
                        padding: 2px 5px !important;
                        font-size: 9px !important;
                        bottom: 6px !important;
                        left: 6px !important;
                    }
                    .hw-rating-star {
                        font-size: 10px !important;
                    }
                    .hw-wishlist-icon-btn {
                        width: 24px !important;
                        height: 24px !important;
                        top: 6px !important;
                        right: 6px !important;
                    }
                    .hw-wishlist-icon-btn svg {
                        width: 13px !important;
                        height: 13px !important;
                    }
                }
                `}
      </style>

      <div className="hw-product-card">
        <div className="hw-image-container">
          <div className="hw-badge-container">
            {is_featured && <div className="hw-ad-badge">AD</div>}
            {displayDiscount && <div className="hw-badge-off">{displayDiscount}</div>}
            {(tag || isHotDeal) && (
              <div className="hw-badge-tag">
                {tag || (isHotDeal ? "best seller" : "")}
              </div>
            )}
          </div>

          {star && !isHovered && (
            <div className="hw-rating-badge">
              <span>{star}</span>
              <AiFillStar className="hw-rating-star" />
              <span className="hw-rating-separator">|</span>
              <span className="hw-rating-count">{cleanRatingCount}</span>
            </div>
          )}

          {/* Carousel dots overlay on hover */}
          {isHovered && displayImages.length > 1 && (
            <div className="hw-carousel-dots">
              {displayImages.map((_, idx) => (
                <div
                  key={idx}
                  className={`hw-dot ${idx === currentImageIndex ? "active" : ""}`}
                />
              ))}
            </div>
          )}

          {/* Similar items button overlay on hover */}
          {/* {isHovered && (
            <div
              className="hw-similar-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="#43c8ca"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="13" height="13" rx="1.5" />
                <rect x="8" y="8" width="13" height="13" rx="1.5" />
              </svg>
            </div>
          )} */}

          <motion.div
            animate={{ x: `-${currentImageIndex * 100}%` }}
            transition={{ type: "tween", duration: 0.4 }}
            style={{
              display: "flex",
              width: `${displayImages.length * 100}%`,
              height: "100%",
            }}
          >
            {displayImages.map((img, idx) => (
              <img
                key={`${img}-${idx}`}
                src={img.replace("w_480,h_640", "w_800,h_1066")}
                alt={`${name} - ${idx}`}
                className="hw-product-image"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = PRODUCT_PLACEHOLDER;
                }}
              />
            ))}
          </motion.div>
        </div>

        <div className="hw-product-info">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              width: "100%",
            }}
          >
            <h3 className="hw-product-brand">{brand?.substring(0, 30)}...</h3>
            <p className="hw-product-subtitle" title={description}>
              {description}
            </p>
          </div>

          <div className="hw-price-row">
            <span className="hw-price-current">
              Rs.{" "}
              {resolvedPrice.toLocaleString("en-IN", {
                minimumFractionDigits: 0,
              })}
            </span>
            {resolvedOriginalPrice > resolvedPrice && (
              <span className="hw-price-original">
                Rs.{" "}
                {resolvedOriginalPrice.toLocaleString("en-IN", {
                  minimumFractionDigits: 0,
                })}
              </span>
            )}
            {savings && displayDiscount && (
              <span className="hw-price-save">
                ({displayDiscount})
              </span>
            )}
          </div>

          {!hideAddToCart && (
            <div className="hw-card-actions-wrapper">
              <button
                className={`hw-btn-add-to-cart`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(linkTo);
                }}
              >
                <>
                  <FaShoppingCart size="11px" /> ADD TO CART
                </>
              </button>
              <button
                className={`hw-btn-wishlist-toggle ${isInWishlist ? "active" : ""}`}
                onClick={handleToggleWishlist}
                title="Add to Wishlist"
              >
                {isInWishlist ? (
                  <AiFillHeart size="16px" />
                ) : (
                  <AiOutlineHeart size="16px" />
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export const ProductCardSkeleton: React.FC = () => {
  return (
    <div
      className="hw-product-card skeleton-container"
      style={{ width: "100%" }}
    >
      <style>
        {`
                .skeleton-container {
                    pointer-events: none;
                }
                .skeleton-image {
                    width: 100%;
                    aspect-ratio: 1 / 0.9;
                    background: #eee;
                    border-radius: 0px;
                    margin-bottom: 12px;
                }
                .skeleton-line {
                    height: 14px;
                    background: #eee;
                    border-radius: 0px;
                    margin-bottom: 8px;
                }
                .shimmer-effect {
                    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                    background-size: 200% 100%;
                    animation: shimmer-anim 1.5s infinite;
                }
                @keyframes shimmer-anim {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                `}
      </style>
      <div className="skeleton-image shimmer-effect" />
      <div className="skeleton-line shimmer-effect" style={{ width: "80%" }} />
      <div className="skeleton-line shimmer-effect" style={{ width: "60%" }} />
      <div className="skeleton-line shimmer-effect" style={{ width: "40%" }} />
    </div>
  );
};

export default ProductCard;
